import React, { useState } from 'react';
import { Drawer, Dropdown } from 'antd';
import { DownOutlined, UpOutlined, MenuOutlined } from '@ant-design/icons';
import RegistrationButton from './RegistrationButton';
import Link from 'next/link';
import { NavLink } from './NavLink';
import { Menu } from 'antd';
import { PARTNERS, ROUTES } from '@/constants/routes';
import PartnerDropdown from "./PartnerDropdown";
import PartnerRow from "./PartnerRow";
import { partners } from "@/data/partners";

interface DropdownMenu {
    label: string;
    href: string;
    key: string;
    external?: boolean;
}

interface NavbarMenu {
    label: string;
    href: string;
    key: string;
    dropdown?: DropdownMenu[];
}

const navbarMenu: NavbarMenu[] = [
    {
        label: "Home",
        key: "home",
        href: "/",
    },
    {
        label: "Philosophy",
        href: "/iit-philosophy",
        key: 'philosophy',
    },
    {
        label: "Event",
        key: "event",
        href: "#",
        dropdown: [
            { label: "Timeline", href: ROUTES.TIMELINE, key: "timeline" },
            { label: "Competition", href: ROUTES.EVENT_COMPETITION, key: "competition" },
            { label: "Panelists", href: ROUTES.EVENT_PANELISTS, key: "panelists" },
            { label: "Workshop", href: ROUTES.WORKSHOP, key: "workshop" },
            { label: "Certificate", href: ROUTES.CERTIFICATE, key: "certificate" },
        ],
    },
    {
        label: "Prizes",
        key: "prizes",
        href: ROUTES.PRIZES,
    },
    {
        label: "Gallery",
        key: "gallery",
        href: "#",
        dropdown: [
            { label: "Photos", href: ROUTES.GALLERY_PHOTOS, key: "photos" },
            { label: "Testimonials", href: ROUTES.GALLERY_TESTIMONIALS, key: "testimonials" },
        ],
    },
    {
        label: "Sponsor",
        key: "sponsor",
        href: ROUTES.SPONSOR,
    },
    {
        label: "Media Partner",
        key: "media-partner",
        href: ROUTES.MEDIA_PARTNER,
    },
    {
        label: "Partner",
        key: "partner",
        href: "#",
        dropdown: PARTNERS.map((p, idx) => ({
            label: p.label,
            href: p.href,
            key: String(idx),
            external: true,
        })),
    },
    {
        label: "Bazaar",
        href: ROUTES.BAZAAR,
        key: 'bazaar',
    },
    {
        label: "News",
        href: ROUTES.NEWS,
        key: 'news',
    },
    { 
        label: "Procedure", 
        href: ROUTES.TERMS, 
        key: "terms" 
    },
];

const beforePartner = navbarMenu.slice(0, navbarMenu.findIndex(i => i.key === "partner"));
const afterPartner = navbarMenu.slice(navbarMenu.findIndex(i => i.key === "partner") + 1);

function toMenuItems(items: NavbarMenu[]) {
    return items.map(item => {
        if (item.dropdown) {
            return {
                key: item.key,
                label: item.label,
                children: item.dropdown.map(sub => ({
                    key: sub.key,
                    label: sub.external ? (
                        <a onClick={() => window.open(sub.href, "_blank", "noreferrer")}>
                            {sub.label}
                        </a>
                    ) : (
                        <Link href={sub.href}>{sub.label}</Link>
                    )
                })),
            };
        }
        return {
            key: item.key,
            label: <NavLink href={item.href}>{item.label}</NavLink>,
        };
    });
}
export const Navbar: React.FC = () => {
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [partnerOpen, setPartnerOpen] = useState(false);

    // const openInNewTab = (url: string) => {
    //     window.open(url, '_blank', 'noreferrer');
    // };

    const toggleDrawer = () => {
        setDrawerVisible(!drawerVisible);
    };

    const toggleDropdown = (key: string) => {
        setOpenDropdownKey(prev => (prev === key ? null : key));
    };

    const onMobileOpenChange = (keys: string[]) => {
        const latestKey = keys[keys.length - 1]; // submenu terakhir yang dibuka
        // jika user buka submenu baru -> tutup semua yang lain
        setOpenKeys(latestKey ? [latestKey] : []);
    };

    return (
        <nav className="bg-white fixed w-full top-0 start-0 shadow z-50">
            {/* DESKTOP VIEW */}
            <div className="mx-auto">
                {/* <div className="flex justify-between items-center px-6 py-1"> */}
                <div className="flex justify-between items-center px-6 py-4">
                    <div className="flex items-center">
                        <Link href="/" className='hidden lg:flex mr-4 shrink-0'>
                            <img alt="Logo" src="/images/IIT-Challenge-Logo-2025.png" className='w-28 h-16 object-contain' />
                        </Link>
                        <ul className="hidden lg:flex lg:ml-6 space-x-6 rtl:space-x-reverse">
                             {navbarMenu.map(item => {
                               if (item.dropdown && item.dropdown.length > 0) {
                                     if (item.key === "partner") {
                                         return (
                                             <Dropdown
                                                 key={item.key}
                                                 dropdownRender={() => <PartnerDropdown />}
                                                 trigger={['click']}
                                                 open={openDropdownKey === item.key}
                                                 onOpenChange={() => toggleDropdown(item.key)}
                                             >
                                                 <a onClick={(e) => e.preventDefault()} className="flex items-center text-gray-500 cursor-pointer">
                                                     {item.label}
                                                     {openDropdownKey === item.key ? (
                                                         <UpOutlined style={{ fontSize: '0.7rem', marginLeft: '0.3rem' }} />
                                                     ) : (
                                                         <DownOutlined style={{ fontSize: '0.7rem', marginLeft: '0.3rem' }} />
                                                     )}
                                                 </a>
                                             </Dropdown>
                                         );
                                     }

                                     return (
                                         <Dropdown
                                             key={item.key}
                                             menu={{
                                                 items: item.dropdown.map(sub => ({
                                                     key: sub.key,
                                                     label: sub.external ? (
                                                         <a onClick={() => window.open(sub.href, "_blank", "noreferrer")}>{sub.label}</a>
                                                     ) : (
                                                         <Link href={sub.href}>{sub.label}</Link>
                                                     )
                                                 }))
                                             }}
                                             trigger={['click']}
                                             open={openDropdownKey === item.key} onOpenChange={() => toggleDropdown(item.key)}
                                         >
                                             <a onClick={(e) => e.preventDefault()} className="flex items-center text-gray-500 cursor-pointer">
                                                 {item.label}
                                                 {openDropdownKey === item.key ? (
                                                     <UpOutlined style={{ fontSize: '0.7rem', marginLeft: '0.3rem' }} />
                                                 ) : (
                                                     <DownOutlined style={{ fontSize: '0.7rem', marginLeft: '0.3rem' }} />
                                                 )}
                                             </a>
                                         </Dropdown>
                                     );
                                 } else {
                                     return (
                                         <NavLink key={item.key} href={item.href}>
                                             {item.label}
                                         </NavLink>
                                     );
                                 }
                             })}
                        </ul>
                        <div className="flex items-center lg:hidden ml-5">
                            <button onClick={toggleDrawer} className="text-gray-500">
                                <MenuOutlined style={{ fontSize: '24px' }} />
                            </button>
                        </div>
                    </div>
                    <Link href="/" className='lg:hidden mr-5'>
                        <img alt="Logo" src="/images/IIT-Challenge-Logo-2025.png" className='w-28 h-16 object-contain' />
                    </Link>

                    <div className="items-center hidden lg:flex">
                        <RegistrationButton />
                    </div>
                </div>
            </div>

            {/* MOBILE VIEW */}
            <Drawer
                placement="left"
                closable={true}
                onClose={toggleDrawer}
                open={drawerVisible}
            >
                <div className="flex flex-col justify-between h-full">
                    <div className="py-4">
                        <Link href="/">
                            <img alt="Logo" src="/images/IIT-Challenge-Logo-2025.png" className='px-2 mb-2 shrink-0 w-28 h-16 object-contain' />
                        </Link>

                        <Menu
                            mode="inline"
                            items={toMenuItems(beforePartner)}
                            inlineIndent={16}
                            className="text-gray-500"
                            openKeys={openKeys}
                            onOpenChange={onMobileOpenChange}
                            onClick={toggleDrawer}
                        />

                        <div>
                            <button
                                className="w-full h-10 flex items-center justify-between pl-5 pr-6 text-[14px] text-gray-500 justify-between"
                                onClick={() => setPartnerOpen(!partnerOpen)}
>
                                Partner
                                {partnerOpen ? <UpOutlined style={{ fontSize: '9px' }} /> : <DownOutlined style={{ fontSize: '9px' }} />}
                            </button>
                            {partnerOpen && (
                                <div className="pl-4 flex flex-col divide-y">
                                    {partners.map(p => (
                                        <PartnerRow key={p.name} partner={p} />
                                    ))}
                                </div>
                            )}
                        </div>

                        <Menu
                            mode="inline"
                            items={toMenuItems(afterPartner)}
                            inlineIndent={16}
                            className="text-gray-500 mb-2"
                            openKeys={openKeys}
                            onOpenChange={onMobileOpenChange}
                            onClick={toggleDrawer}
                        />
                </div>

                <div className="py-4">
                    <RegistrationButton />
                </div>
            </div>
        </Drawer>
        </nav>
    );
}
